import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  LinearProgress,
  Box,
  Avatar,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import usePortalAuth from '../../../../auth/usePortalAuth';
import basePCUrl from '../../../../Globals';

import TrainingCompleteModal from './TrainingCompleteModal';
import TrainingFailModal from './TrainingFailModal';
import Cookies from 'js-cookie';

const LETTER_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === 'dark';
  // The app's global dark-mode rule forces text to inherit white, so dark-mode
  // readability here depends on keeping the surfaces behind that text dark.
  const subtleBorder = isDark ? 'rgba(255, 255, 255, 0.12)' : '#e2e8f0';
  return {
  outerCard: {
    maxWidth: 880,
    margin: '0 auto',
    borderRadius: 16,
    border: `1px solid ${subtleBorder}`,
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
  },
  backButton: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    padding: theme.spacing(1, 2.5),
    marginBottom: theme.spacing(2),
    backgroundColor: '#2B6CB0',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#2c5282',
    },
  },
  progressRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: theme.spacing(0.5),
  },
  progressBar: {
    height: 10,
    borderRadius: 5,
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#e2e8f0',
    '& .MuiLinearProgress-bar': {
      borderRadius: 5,
      backgroundColor: '#2B6CB0',
    },
  },
  answeredLabel: {
    textAlign: 'center',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  questionCard: {
    borderRadius: 12,
    border: `1px solid ${subtleBorder}`,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  questionText: {
    fontWeight: 700,
    fontSize: '1.15rem',
    lineHeight: 1.5,
  },
  optionCard: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: 12,
    border: `1px solid ${subtleBorder}`,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1.5),
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      borderColor: '#90cdf4',
      backgroundColor: isDark ? 'rgba(144, 205, 244, 0.10)' : '#f7fafc',
    },
  },
  optionCardSelected: {
    borderColor: '#2B6CB0',
    backgroundColor: isDark ? 'rgba(43, 108, 176, 0.32)' : '#ebf4ff',
    boxShadow: '0 0 0 2px rgba(43,108,176,0.2)',
  },
  letterBadge: {
    width: 36,
    height: 36,
    fontSize: '0.85rem',
    fontWeight: 700,
    backgroundColor: isDark ? '#384252' : '#e8edf4',
    color: isDark ? '#e2e8f0' : '#4a5568',
    marginRight: theme.spacing(2),
    flexShrink: 0,
  },
  letterBadgeSelected: {
    backgroundColor: '#2B6CB0',
    color: '#fff',
  },
  optionText: {
    fontSize: '0.95rem',
  },
  footer: {
    borderRadius: 12,
    backgroundColor: isDark ? 'rgba(43, 108, 176, 0.18)' : '#e8f0fe',
    padding: theme.spacing(2, 3),
    marginTop: theme.spacing(1),
  },
  hintRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    color: isDark ? '#cbd5e0' : '#4a5568',
    fontSize: '0.9rem',
  },
  navRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  prevButton: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    padding: theme.spacing(1, 2.5),
    backgroundColor: '#2B6CB0',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#2c5282',
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.92)',
      backgroundColor: 'rgba(43, 108, 176, 0.6)',
    },
    '&.Mui-disabled .MuiSvgIcon-root': {
      color: 'rgba(255, 255, 255, 0.92)',
    },
  },
  nextButton: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    padding: theme.spacing(1, 2.5),
  },
  dotRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 6,
    marginTop: theme.spacing(2),
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#cbd5e0',
    transition: 'background-color 0.2s',
  },
  dotActive: {
    backgroundColor: '#2B6CB0',
  },
  dotAnswered: {
    backgroundColor: '#90cdf4',
  },
  };
});

const UpdatedTrainingQuiz = ({ quizData, trainingId, trainingVideo, history }) => {
  const classes = useStyles();
  const totalQuestions = Object.keys(quizData).length;
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [modal, setModal] = useState(false);
  const [failModal, setFailModal] = useState(false);

  const answeredCount = Object.keys(answers).length;
  const isNextDisabled = selectedAnswer === null;

  const handleAnswerSelect = (value) => {
    setSelectedAnswer(value);
  };

  const handleNextQuestion = () => {
    if (selectedAnswer === null) return;

    const updated = { ...answers, [currentQuestion]: parseInt(selectedAnswer) };
    setAnswers(updated);

    if (selectedAnswer === quizData[currentQuestion].AnswerKey.toString()) {
      setScore(score + 1);
    }

    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(currentQuestion + 1);
      const nextSaved = updated[currentQuestion + 1];
      setSelectedAnswer(nextSaved !== undefined ? nextSaved.toString() : null);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestion <= 1) return;
    const prev = currentQuestion - 1;
    setCurrentQuestion(prev);
    const saved = answers[prev];
    setSelectedAnswer(saved !== undefined ? saved.toString() : null);
  };

  const submitFinalAnswers = () => {
    const finalAnswers = { ...answers, [currentQuestion]: parseInt(selectedAnswer) };
    const ordered = [];
    for (let i = 1; i <= totalQuestions; i++) {
      ordered.push(finalAnswers[i] !== undefined ? finalAnswers[i] : -1);
    }
    saveTrainingProgress(ordered);
  };

  const isLastQuestion = currentQuestion === totalQuestions;


  function isQuizPassed(userAnswers) {
    const correctAnswersCount = userAnswers.filter((answer, index) => {
      // Compare user's answer to the correct answer for each question (Assuming 0-based index)
      return answer === quizData[index + 1].AnswerKey
    }).length;

    return correctAnswersCount >= 8;
  }

var correctAnswersCount

  function calculateScore(quizData, userAnswers) {
    const totalQuestions = Object.keys(quizData).length;
    correctAnswersCount = 0;
  
    for (let i = 1; i <= totalQuestions; i++) {
      if (userAnswers[i - 1] === quizData[i].AnswerKey) {
        correctAnswersCount++;
      }
    }
    const percentageScore = ((correctAnswersCount / totalQuestions) * 100).toFixed(1);
    return parseFloat(percentageScore);
  }

  function setTrainingCookie(trainingId, trainingStatus) {
    const expirationTimeInMinutes = 30;
    const expirationTimeInMilliseconds = expirationTimeInMinutes * 60 * 1000;
  
    // Set the cookie with the calculated expiration time
    Cookies.set(trainingId, trainingStatus, { expires: new Date(Date.now() + expirationTimeInMilliseconds) });
  }
  

  const toggle = () => {
    setModal(!modal);
  };

  const failToggle = () => {
    setFailModal(!failModal);
    window.scrollTo({ top: 100, left: 100, behavior: 'smooth' });
  };

  function markTrainingAsComplete(trainingTitle) {
    // Store the training title with the value "complete" in local storage
    localStorage.setItem(trainingTitle, "complete");
    console.log(`Training "${trainingTitle}" marked as complete.`);
}

  const { getAccessTokenSilently } = usePortalAuth();
  const userAnswersArray = Object.keys(answers).sort((a, b) => a - b).map((k) => answers[k]);
  const quizPassed = isQuizPassed(userAnswersArray);
  const saveTrainingProgress = async (finalQuizAnswers) => {
    window.scrollTo({ top: 100, left: 100, behavior: 'smooth' });
    
    if (calculateScore(quizData, finalQuizAnswers) >= 80){
      setModal(true);
      setTrainingCookie(trainingId, 'Completed')
    } else {
      setFailModal(true);
    }
    // if (quizPassed === true) {
    //   setModal(true);
    // } else {
    //   setFailModal(true);
    // }

    const token = await getAccessTokenSilently();

    var data = {
      modId: trainingId,
      quizResults: finalQuizAnswers,
      videoProgress: 0,
    };

    fetch(basePCUrl, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        Authorization: `Bearer ${token}`,
        Action: 'updateUserTraining',
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (response.status !== 200) {
          console.log('Looks like there was a problem. Status Code: ' + response.status);
          return;
        }

        response.json().then((data) => {
          return;
        });
      })
      .catch(function (err) {
        console.log('Fetch Error :-S', err);
      });
  };

  const progressPercent = (currentQuestion / totalQuestions) * 100;

  const buildFinalAnswers = () => {
    const finalAnswers = { ...answers, [currentQuestion]: parseInt(selectedAnswer) };
    const ordered = [];
    for (let i = 1; i <= totalQuestions; i++) {
      ordered.push(finalAnswers[i] !== undefined ? finalAnswers[i] : -1);
    }
    return ordered;
  };

  return (
    <Paper elevation={0} className={classes.outerCard}>
      {/* Back to training overview */}
      <Button
        className={classes.backButton}
        startIcon={<ArrowBackIcon />}
        onClick={() => history.push('/Dashboard/TrainingDashboard')}
      >
        Return to Training Overview
      </Button>

      {/* Progress */}
      <Box className={classes.progressRow}>
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
          Question {currentQuestion} of {totalQuestions}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {Math.round(progressPercent)}% complete
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progressPercent}
        className={classes.progressBar}
      />
      <Typography className={classes.answeredLabel}>
        {answeredCount} of {totalQuestions} questions answered
      </Typography>

      {/* Question card */}
      <Paper elevation={0} className={classes.questionCard}>
        <Typography className={classes.questionText}>
          {quizData[currentQuestion].Question}
        </Typography>
      </Paper>

      {/* Answer options */}
      {quizData[currentQuestion].AnswerList.map((answer, index) => {
        const isSelected = selectedAnswer === index.toString();
        return (
          <Box
            key={index}
            className={`${classes.optionCard} ${isSelected ? classes.optionCardSelected : ''}`}
            onClick={() => handleAnswerSelect(index.toString())}
          >
            <Avatar
              className={`${classes.letterBadge} ${isSelected ? classes.letterBadgeSelected : ''}`}
            >
              {LETTER_LABELS[index] || index + 1}
            </Avatar>
            <Typography className={classes.optionText}>{answer}</Typography>
          </Box>
        );
      })}

      {/* Footer */}
      <Box className={classes.footer}>
        {isNextDisabled && (
          <Box className={classes.hintRow}>
            <RadioButtonUncheckedIcon fontSize="small" />
            <span>Select an answer to continue</span>
          </Box>
        )}
        <Box className={classes.navRow}>
          <Button
            className={classes.prevButton}
            startIcon={<ArrowBackIcon />}
            disabled={currentQuestion <= 1}
            onClick={handlePreviousQuestion}
          >
            Previous
          </Button>
          {isLastQuestion ? (
            <Button
              variant="contained"
              color="primary"
              className={classes.nextButton}
              disabled={isNextDisabled}
              onClick={submitFinalAnswers}
              endIcon={<ArrowForwardIcon />}
            >
              Submit Quiz
            </Button>
          ) : (
            <Button
              variant="outlined"
              className={classes.nextButton}
              disabled={isNextDisabled}
              onClick={handleNextQuestion}
              endIcon={<ArrowForwardIcon />}
            >
              Next Question
            </Button>
          )}
        </Box>
      </Box>

      {/* Dot pagination */}
      <Box className={classes.dotRow}>
        {Array.from({ length: totalQuestions }, (_, i) => {
          const qNum = i + 1;
          let dotClass = classes.dot;
          if (qNum === currentQuestion) dotClass += ` ${classes.dotActive}`;
          else if (answers[qNum] !== undefined) dotClass += ` ${classes.dotAnswered}`;
          return <span key={qNum} className={dotClass} />;
        })}
      </Box>

      <TrainingCompleteModal
        toggle={toggle}
        modal={modal}
        history={history}
        scorePercentage={calculateScore(quizData, buildFinalAnswers())}
        quizLength={totalQuestions}
        correctAnswersCount={correctAnswersCount}
      />
      <TrainingFailModal
        toggle={failToggle}
        modal={failModal}
        history={history}
        trainingId={trainingId}
        trainingVideo={trainingVideo}
        setCurrentQuestion={setCurrentQuestion}
        setSelectedAnswer={setSelectedAnswer}
        setUserAnswers={(arr) => {
          const map = {};
          arr.forEach((a, i) => { map[i + 1] = a; });
          setAnswers(map);
        }}
        setScore={setScore}
        setIsNextDisabled={() => {}}
        scorePercentage={calculateScore(quizData, buildFinalAnswers())}
        quizLength={totalQuestions}
        correctAnswersCount={correctAnswersCount}
      />
    </Paper>
  );
};

export default UpdatedTrainingQuiz;
